import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import { MailOutlined, ReadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Tabs } from 'antd';
import qs from 'qs';
import moment from 'moment';
import HtmlParser from 'react-html-parser';

import { fetchNotifications, markNotificationRead, markNotificationUnread } from '@/redux/actions/notificationsActions';
import DynamicTable from '@/components/DynamicTable';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import { cleanObject, isValidDate } from '@/utils/helpers';
import BaseSelect from '@/components/Elements/BaseSelect';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/notifications', breadcrumbName: 'Quản lý thông báo' },
];

export default function NotificationList() {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const items = useSelector((state) => state.notificationsReducer.list.data || []);
    const pagination = useSelector((state) => state.notificationsReducer.list.pagination || {});

    const [isLoading, setIsLoading] = useState(false);
    const query = qs.parse(location.search.slice(1));

    const loadData = useCallback((q = query) => {
        console.log('q: ', q);
        setIsLoading(true);
        dispatch(fetchNotifications(q)).finally(() => setIsLoading(false));
    }, [dispatch, query]);

    useEffect(() => {
        loadData();
    }, [location.search]);

    const formatDate = (d) =>
        moment.isMoment(d) && d.isValid() ? d.format('YYYY-MM-DD') : undefined;

    const handleFilterChange = (name, value) => {
        let q = { ...query, [name]: value };

        const rangeFilters = {
            start_date: ['start_date_from', 'start_date_to'],
            created_at: ['created_at_from', 'created_at_to'],
        };

        if (rangeFilters[name]) {
            const [fromKey, toKey] = rangeFilters[name];
            const [start, end] = value || [];

            q[fromKey] = formatDate(start);
            q[toKey] = formatDate(end);

            if (!q[fromKey] && !q[toKey]) {
                delete q[fromKey];
                delete q[toKey];
            }
            delete q[name];
        }

        q = cleanObject(q);
        history.push({ pathname: location.pathname, search: qs.stringify(q) });
    };

    const columns = [
        {
            title: 'Tiêu đề',
            dataIndex: 'title',
            key: 'title',
            width: '250px',
            render: (text, record) => (
                <a
                    href={record?.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        e.stopPropagation(); // tránh kích hoạt event onRow
                        if (!record.read_at) {
                            // chỉ đánh dấu đã đọc nếu chưa đọc
                            dispatch(markNotificationRead(record.id));
                        }
                    }}
                    style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 500 }}
                >
                    {record.title}
                </a>
            ),
        },
        {
            title: 'Nội dung',
            dataIndex: 'message',
            key: 'message',
            width: 300,
            render: (_, record) => (
                <span style={{ whiteSpace: 'normal' }}>{HtmlParser(record.message)}</span>
            ),
        },
        {
            title: 'Ngày gửi',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (val) => (val ? isValidDate(val, true) : null),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'read_at',
            key: 'read_at',
            width: 180,
            align: 'center',
            render: (read_at) => {
                if (!read_at) {
                    return <span style={{ color: '#cf1322', fontWeight: 500 }}>Chưa đọc</span>;
                }
                return (
                    <span style={{ color: '#389e0d' }}>
                        Đã dọc lúc {moment(read_at).format('HH:mm DD/MM/YYYY')}
                    </span>
                );
            },
        },
        {
            title: 'Người gửi',
            dataIndex: 'sender_name',
            key: 'sender_name',
            render: (_, record) => <span>{record.sender_name || '—'}</span>,
        },
        {
            title: 'Hành động',
            key: 'actions',
            width: 150,
            align: 'center',
            render: (_, record) => {
                if (!record.read_at) {
                    // Đang chưa đọc → không cần nút
                    return <span style={{ color: '#cf1322', fontWeight: 500 }}>—</span>;
                }

                return (
                    <Button
                        size="small"
                        type="link"
                        style={{ padding: 0, color: '#1677ff' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(markNotificationUnread(record.id));
                        }}
                    >
                        Đánh dấu chưa đọc
                    </Button>
                );
            }
        }

    ];

    return (
        <div>
            <PageTitle routes={routes} title="Quản lý thông báo" />
            <TableActionBar
                isShowAddButton={false}
                isShowDeleteButton={false}
                showSearch
                showFilter={false}
                onSearch={(value) => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                placeholderSearch="Tìm kiếm tiêu đề, nội dung..."
            >
                <BaseRangeFilter
                    name="created_at"
                    query={query}
                    onChangeFilter={handleFilterChange}
                    placeholder={['Từ ngày', 'Đến ngày']}
                    width="300px"
                />
                <Divider type="vertical" />
                <BaseSelect
                    options={[
                        { value: 'telesale', label: 'Telesale' },
                        { value: 'consultant', label: 'Tư vấn trực tiếp' },
                        { value: 'milestone', label: 'Ca Invisalign' },
                    ]}
                    placeholder="Lọc theo thông báo"
                    style={{ width: 200 }}
                    onChange={(value) => handleFilterChange('module_key', value)}
                    defaultValue={query.module_key ? query.module_key : null}
                />
                <Divider type="vertical" />
                <Button
                    icon={<ReloadOutlined />}
                    loading={isLoading}
                    onClick={() => loadData()}
                >
                    Refresh
                </Button>
            </TableActionBar>

            <Card size='small' bodyStyle={{ padding: 0 }} className='has-custom-column-table-btn mt-3'>
                <Tabs
                    activeKey={query.read_status || 'unread'}
                    onChange={(key) => {
                        const q = { ...query, read_status: key };
                        history.push({
                            pathname: location.pathname,
                            search: qs.stringify(cleanObject(q)),
                        });
                    }}
                    size='small'
                    items={[
                        {
                            key: 'unread',
                            label: <span> <MailOutlined style={{ color: '#1677ff', marginRight: 6 }} /> Chưa đọc</span>,
                        },
                        {
                            key: 'read',
                            label: <span><ReadOutlined style={{ color: '#52c41a', marginRight: 6 }} /> Đã đọc</span>,
                        },
                    ]}
                    type="card"
                />

                <DynamicTable
                    dataSource={items || []}
                    columns={columns}
                    isLoading={isLoading}
                    pagination={pagination}
                    rowSelection={false}
                    moduleKey="notifications_list"
                />
            </Card>
        </div>
    );
}
